import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { useAuth } from "./AuthContext";

const ChatContext = createContext(null);

export const ChatProvider = ({ children }) => {
  const { user } = useAuth();
  const [activeGroupId, setActiveGroupId] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const pollIntervalRef = useRef(null);

  // Polling mechanism: Increments refreshTrigger every 30 seconds
  useEffect(() => {
    if (!user) {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      return;
    }

    // Initial trigger
    setRefreshTrigger((prev) => prev + 1);

    // Set interval for 30 seconds
    pollIntervalRef.current = setInterval(() => {
      setRefreshTrigger((prev) => prev + 1);
    }, 30000);

    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [user]);

  const joinGroup = useCallback((groupId) => {
    setActiveGroupId(groupId);
  }, []);

  const leaveGroup = useCallback((groupId) => {
    setActiveGroupId((prev) => (prev === groupId ? null : prev));
  }, []);

  // Handlers for compatibility (No-ops in polling mode)
  const sendSocketMessage = useCallback(() => {
    return Promise.reject(new Error("Socket.IO is disabled. Use REST API."));
  }, []);

  const emitTyping = useCallback(() => {
    // Typing indicators are not supported in 30s polling
  }, []);

  const emitRead = useCallback(() => {
    // Read markers can be sent via REST API calls
  }, []);

  const on = useCallback(() => {
    // Events are replaced by polling refreshTrigger
    return () => {}; // Return a dummy cleanup function
  }, []);

  const refreshManually = useCallback(() => {
    setRefreshTrigger((prev) => prev + 1);
  }, []);

  return (
    <ChatContext.Provider
      value={{
        isConnected: !!user, // Always "connected" if logged in
        activeGroupId,
        refreshTrigger,
        joinGroup,
        leaveGroup,
        sendSocketMessage,
        emitTyping,
        emitRead,
        on,
        refreshManually,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error("useChat must be used within a ChatProvider");
  return ctx;
};
